<script setup lang="ts">
    import { computed, nextTick, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import { onKeyStroke, refDebounced, useMediaQuery } from '@vueuse/core'
    import type { AgentPublic, Chat as Conversation } from 'models'
    import {
        getConversationPreview,
        useChatbotStore,
        useConversationActions,
    } from 'composables'
    import PkAgentSelectorInline from './PkAgentSelectorInline.vue'
    import PkAvatar from './PkAvatar.vue'
    import PkChatSidebarConversationItem from './PkChatSidebarConversationItem.vue'

    const props = defineProps<{
        agentId: string
        /** Public agents of the organization, shown in the agent selector */
        agents?: AgentPublic[]
        /** Organization branding, shown at the top */
        organizationName?: string
        organizationLogo?: string | null
        /** Accent color for the active conversation highlight */
        accentColor?: string
    }>()

    const collapsed = defineModel<boolean>('collapsed', { default: false })

    // Below the desktop breakpoint the sidebar is a drawer overlay:
    // selecting anything closes it
    const isOverlay = useMediaQuery('(max-width: 1023px)')
    const closeOnOverlay = () => {
        if (isOverlay.value) {
            collapsed.value = true
        }
    }

    const emit = defineEmits<{
        'select-agent': [agentId: string]
        /** View a conversation's uploaded files (host opens the files panel) */
        'view-files': [chatId: string]
    }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    // === Conversations (shared chatbot store, same instance as PkChatbot) ===
    const store = useChatbotStore(props.agentId)
    const { conversations, isLoadingConversations, localChatId } =
        storeToRefs(store)
    const { startNewChat, navigate, renameChatTitle, deleteChat } = store

    const {
        editingId,
        editingTitle,
        startEdit,
        confirmEdit,
        cancelEdit,
        onRenameKeydown,
        requestDelete,
    } = useConversationActions({ renameChatTitle, deleteChat })

    // === Search ===
    const searchQuery = ref('')
    const debouncedQuery = refDebounced(searchQuery, 300)
    const filteredConversations = computed(() => {
        const query = debouncedQuery.value.trim().toLowerCase()
        if (!query) {
            return conversations.value
        }
        return conversations.value.filter((conversation) =>
            getConversationPreview(conversation).toLowerCase().includes(query),
        )
    })

    // === Time grouping ===
    const DAY_MS = 86_400_000
    const groupedConversations = computed(() => {
        const startOfToday = new Date().setHours(0, 0, 0, 0)
        const buckets: Record<string, Conversation[]> = {
            today: [],
            yesterday: [],
            last7Days: [],
            last30Days: [],
            older: [],
        }
        for (const conversation of filteredConversations.value) {
            // lastMessageAt is typed as Date but arrives as an ISO string at
            // runtime (the chatbot API client doesn't parse through Zod)
            const time = conversation.lastMessageAt
                ? new Date(conversation.lastMessageAt).getTime()
                : 0
            if (time >= startOfToday) {
                buckets.today.push(conversation)
            } else if (time >= startOfToday - DAY_MS) {
                buckets.yesterday.push(conversation)
            } else if (time >= startOfToday - 7 * DAY_MS) {
                buckets.last7Days.push(conversation)
            } else if (time >= startOfToday - 30 * DAY_MS) {
                buckets.last30Days.push(conversation)
            } else {
                buckets.older.push(conversation)
            }
        }
        return Object.entries(buckets)
            .filter(([, items]) => items.length > 0)
            .map(([key, items]) => ({ key, label: $t(`label.${key}`), items }))
    })

    const openConversation = (id: string) => {
        localChatId.value = id
        navigate('chat')
        closeOnOverlay()
    }

    const handleNewChat = () => {
        // startNewChat() already navigates back to the chat view
        startNewChat()
        closeOnOverlay()
    }

    // === Overlay behavior (tablet/mobile: drawer with scrim) ===
    const isDrawerOpen = computed(() => isOverlay.value && !collapsed.value)
    const sidebarEl = ref<HTMLElement>()
    let lastFocusedElement: HTMLElement | null = null

    const getFocusableElements = () =>
        Array.from(
            sidebarEl.value?.querySelectorAll<HTMLElement>(
                'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])',
            ) ?? [],
        ).filter((el) => !el.hasAttribute('disabled'))

    // Open: move focus inside the drawer. Close: restore it to the trigger.
    watch(isDrawerOpen, async (open) => {
        if (open) {
            lastFocusedElement =
                document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null
            await nextTick()
            getFocusableElements()[0]?.focus()
        } else {
            lastFocusedElement?.focus()
            lastFocusedElement = null
        }
    })

    onKeyStroke('Escape', () => {
        if (isDrawerOpen.value) {
            collapsed.value = true
        }
    })

    // Minimal focus trap: keep Tab cycling inside the open drawer
    const onTrapTab = (event: KeyboardEvent) => {
        if (event.key !== 'Tab' || !isDrawerOpen.value) {
            return
        }
        const focusable = getFocusableElements()
        if (focusable.length === 0) {
            return
        }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }
</script>

<template>
    <Transition name="pk-chat-sidebar-backdrop">
        <div
            v-if="isDrawerOpen"
            class="pk-chat-sidebar__backdrop"
            aria-hidden="true"
            @click="collapsed = true" />
    </Transition>
    <aside
        ref="sidebarEl"
        class="pk-chat-sidebar"
        :class="{ 'pk-chat-sidebar--collapsed': collapsed }"
        :style="
            accentColor ? { '--chat-sidebar-accent': accentColor } : undefined
        "
        :inert="collapsed"
        :aria-label="$t('label.conversations')"
        @keydown="onTrapTab">
        <!-- Organization branding, like the app sidebar logo row -->
        <header class="pk-chat-sidebar__header">
            <PkAvatar
                v-if="organizationLogo || organizationName"
                modifiers="surface ring"
                class="pk-chat-sidebar__org-logo"
                :img-src="organizationLogo"
                :name="organizationName" />
            <strong
                v-if="organizationName"
                class="pk-chat-sidebar__org-name"
                :title="organizationName">
                {{ organizationName }}
            </strong>
            <VvButton
                class="ml-auto"
                :icon="isOverlay ? 'ri:close-line' : 'ri:sidebar-fold-line'"
                modifiers="action-quiet"
                :aria-expanded="!collapsed"
                :title="$t('action.closeSidebar')"
                @click="collapsed = !collapsed" />
        </header>

        <div class="pk-chat-sidebar__actions">
            <PkAgentSelectorInline
                v-if="agents"
                :agents
                :model-value="agentId"
                @update:model-value="emit('select-agent', $event!)" />
            <!-- Quiet row, not a filled button: the page's primary action
                 is the message input, everything else stays subordinate -->
            <button
                type="button"
                class="pk-chat-sidebar__new-chat"
                @click="handleNewChat">
                <VvIcon name="ri:edit-box-line" />
                {{ $t('action.startNewChat') }}
            </button>
            <!-- Host-provided extra actions (e.g. "My files" link in the app),
                 styled to match `__new-chat`. Empty in widget/public hosts. -->
            <slot name="actions-extra" />
            <VvInputText
                v-model="searchQuery"
                name="conversation-search"
                modifiers="search"
                icon="ri:search-line"
                icon-position="before"
                :placeholder="$t('placeholder.search')" />
        </div>

        <!-- Loading skeleton -->
        <div
            v-if="isLoadingConversations && conversations.length === 0"
            class="pk-chat-sidebar__list vv-skeleton">
            <div
                v-for="i in 6"
                :key="i"
                class="vv-skeleton__item rounded-md h-36" />
        </div>

        <!-- Grouped conversations -->
        <nav v-else class="pk-chat-sidebar__list">
            <p
                v-if="groupedConversations.length === 0"
                class="pk-chat-sidebar__no-results">
                {{ $t('message.noConversations') }}
            </p>
            <div
                v-for="group in groupedConversations"
                :key="group.key"
                class="pk-chat-sidebar__group">
                <span class="pk-chat-sidebar__group-label">
                    {{ group.label }}
                </span>
                <PkChatSidebarConversationItem
                    v-for="conversation in group.items"
                    :key="conversation.id"
                    v-model:editing-title="editingTitle"
                    :conversation="conversation"
                    :active="conversation.id === localChatId"
                    :editing="editingId === conversation.id"
                    @select="openConversation(conversation.id)"
                    @edit-start="
                        startEdit(
                            conversation,
                            getConversationPreview(conversation),
                        )
                    "
                    @edit-confirm="confirmEdit(conversation.id)"
                    @edit-cancel="cancelEdit"
                    @edit-keydown="onRenameKeydown($event, conversation.id)"
                    @delete="requestDelete(conversation.id)"
                    @files="emit('view-files', conversation.id)" />
            </div>
        </nav>

        <!-- User area, injected by the host (owns session/auth) -->
        <footer v-if="$slots.footer" class="pk-chat-sidebar__footer">
            <slot name="footer" />
        </footer>
    </aside>
</template>

<style lang="scss">
    .pk-chat-sidebar {
        display: flex;
        flex-direction: column;
        width: var(--spacing-256);
        height: 100%;
        flex-shrink: 0;
        overflow: hidden;
        background-color: var(--color-surface-1);
        border-right: 1px solid var(--color-surface-3);
        transition: width 200ms var(--ease-out);

        &--collapsed {
            width: 0;
            border-right: none;
        }

        &__backdrop {
            position: fixed;
            inset: 0;
            z-index: 19;
            background-color: rgb(0 0 0 / 50%);
        }

        &-backdrop-enter-active,
        &-backdrop-leave-active {
            transition: opacity 200ms var(--ease-out);
        }

        &-backdrop-enter-from,
        &-backdrop-leave-to {
            opacity: 0;
        }

        // Same proportions as the app sidebar logo row
        &__header {
            display: flex;
            align-items: center;
            gap: var(--spacing-12);
            height: 50px;
            flex-shrink: 0;
            padding: var(--spacing-10);
        }

        &__org-logo {
            width: var(--spacing-28);
            height: var(--spacing-28);
            font-size: var(--text-12);
            flex-shrink: 0;
        }

        &__org-name {
            font-size: var(--text-14);
            font-weight: var(--font-semibold);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__actions {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-8);
            padding: var(--spacing-10);
        }

        &__new-chat {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: 100%;
            min-height: var(--spacing-40);
            padding: var(--spacing-6) var(--spacing-8);
            border: none;
            border-radius: var(--rounded-md);
            background: none;
            color: var(--color-word-1);
            font-size: var(--text-14);
            text-align: left;
            cursor: pointer;
            transition: var(--transition-colors);

            &:hover {
                background-color: var(--color-surface-2);
            }

            &:focus-visible {
                outline: 1px solid var(--color-surface-5);
            }
        }

        &__list {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-sm);
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            padding: var(--spacing-8);
            scrollbar-width: thin;
            scrollbar-color: var(--color-word-5) var(--color-surface-1);
        }

        &__group {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
        }

        &__group-label {
            display: block;
            padding: var(--spacing-6) var(--spacing-10);
            font-size: var(--text-14);
            font-weight: var(--font-semibold);
            color: var(--color-word-2);
        }

        &__no-results {
            padding: var(--spacing-8);
            font-size: var(--text-14);
            color: var(--color-word-3);
        }

        &__footer {
            border-top: 1px solid var(--color-surface-3);
            padding: var(--spacing-10);
        }

        // Tablet/mobile: the sidebar becomes a drawer overlay. Only
        // `transform` is animated (no width/left reflows).
        @media (max-width: 1023px) {
            position: fixed;
            inset-block: 0;
            left: 0;
            z-index: 20;
            width: min(280px, 85vw);
            height: 100dvh;
            padding-bottom: env(safe-area-inset-bottom);
            border-right: 1px solid var(--color-surface-3);
            transition: transform 200ms var(--ease-out);

            &--collapsed {
                width: min(280px, 85vw);
                transform: translateX(-100%);
                visibility: hidden;
                transition:
                    transform 200ms var(--ease-out),
                    visibility 0s 200ms;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            transition: none;

            &--collapsed {
                transition: none;
            }

            &-backdrop-enter-active,
            &-backdrop-leave-active {
                transition: none;
            }
        }
    }
</style>
